import React from 'react';
import I18n from 'i18n-js';
import { tv } from 'tailwind-variants';
import cn from '@/utils/cn';
import useFlags from '@/hooks/useFlags';
import useLessonOverview from '@/hooks/api/useLessonOverview';
import Header from './Header';
import AssessWith from './AssessWith';
import useLessonOverviewContext from './useLessonOverviewContext';
import NewLessonOverviewStoreProvider from './NewLessonOverviewStoreProvider';
import { NewLessonOverviewProps } from './types';
import DiveDeeper from './DiveDeeper';
import Libraries from './Libraries';
import SlideGroupSection from './SlideGroupSection';
import LessonOptions from './LessonOptions';

const contentStyles = tv({
  base: '',
  variants: {
    reflow: {
      true: 'tw-px-4 card-2col:tw-px-8 reflow-narrow:tw-px-8',
    },
  },
});

const footerStyles = tv({
  base: 'tw-bg-light-grey tw-flex',
  variants: {
    reflow: {
      true: 'tw-flex-col lg:tw-flex-row',
    },
  },
});

const NewLessonOverview = () => {
  const { accessibilityReflow } = useFlags();
  const isStaffer = useLessonOverviewContext(state => state.isStaffer);
  const lesson = useLessonOverviewContext(state => state.lesson);
  const sections = useLessonOverviewContext(state => state.sections);
  const introSlideGroups = useLessonOverviewContext(state => state.introSlideGroups());
  const finalSlideGroups = useLessonOverviewContext(state => state.finalSlideGroups());
  const allSlideShows = useLessonOverviewContext(state => state.allSlideShows());
  const hasAssessments = useLessonOverviewContext(state => state.hasAssessments);
  const elementary = useLessonOverviewContext(state => state.program.elementary);

  const shouldRenderLessonOptions = (
    allSlideShows.length > 0
    || sections.filter(s => s.kind === 'default' && s.investigation === false)
  );

  const shouldRenderAssessWith = () => (
    sections.flatMap(section => section.notebooks).length > 0
    || lesson.lessonGameAssignables?.length > 0
    || (hasAssessments || isStaffer)
  );

  // Tracks *visible* subsections so we know what to number them as in the page structure
  // Matches the conditions used to render each subsection
  const subsectionNumberMap = [
    'header',
    introSlideGroups.length > 0 ? 'introduce_the_lesson' : null,
    shouldRenderLessonOptions ? 'lesson_options' : null,
    shouldRenderAssessWith() ? 'assess_with' : null,
    finalSlideGroups.length > 0 ? 'put_it_all_together' : null,
  ].filter(Boolean);

  // AIDEV-NOTE: PLAT-17301 — grade-band typography. The `k5` class scopes the `.k5 .heading*`/`.labels`/`.body`
  // overrides in stylesheets/components/_text.scss (Poppins weights/line-heights) to this subtree for K-5 programs;
  // 6-12 programs omit it and inherit the base (Lato) token values. All <Text> descendants pick this up via cascade.
  return (
    <div className={cn('tw-bg-transparent', { k5: elementary })}>
      <div className={contentStyles({ reflow: accessibilityReflow })}>
        <Header />
        {introSlideGroups.length > 0 && (
          <SlideGroupSection
            slideGroups={introSlideGroups}
            subheaderNumber={subsectionNumberMap.indexOf('introduce_the_lesson')}
            title={I18n.t('introduce_the_lesson')}
          />
        )}
        {shouldRenderLessonOptions && (
          <LessonOptions
            subheaderNumber={subsectionNumberMap.indexOf('lesson_options')}
          />
        )}
        {shouldRenderAssessWith() && (
          <AssessWith
            subheaderNumber={subsectionNumberMap.indexOf('assess_with')}
          />
        )}
        {finalSlideGroups.length > 0 && (
          <SlideGroupSection
            slideGroups={finalSlideGroups}
            subheaderNumber={subsectionNumberMap.indexOf('put_it_all_together')}
            title={I18n.t('put_it_all_together')}
          />
        )}
      </div>
      <footer className={footerStyles({ reflow: accessibilityReflow })}>
        <DiveDeeper />
        <Libraries />
      </footer>
    </div>
  );
};

const NewLessonOverviewWrapper = (props: NewLessonOverviewProps) => {
  const { programId, lessonId, locale } = props;
  const lessonOverviewData = useLessonOverview({ lessonId, locale, programId });

  if (lessonOverviewData.isLoading) return null;

  return (
    <NewLessonOverviewStoreProvider
      props={{ ...props, ...lessonOverviewData }}
    >
      <NewLessonOverview />
    </NewLessonOverviewStoreProvider>
  );
};

export default NewLessonOverviewWrapper;
